import { fetchOrganizations } from 'admin/actions/organization';
import { Button } from 'admin/components/atoms/Buttons';
import DateTimeRange from 'admin/components/atoms/DateTimeRange';
import Label from 'admin/components/atoms/Label';
import Labeled from 'admin/components/atoms/Labeled';
import Select from 'admin/components/atoms/Select';
import LabeledSelect from 'admin/components/molecules/LabeledSelect';
import LabeledTextBox from 'admin/components/molecules/LabeledTextBox';
import { toQueryParams } from 'admin/utils';
import moment, { Moment } from 'moment';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { QueryParamConfig, StringParam, useQueryParams, withDefault } from 'use-query-params';
import { useAppSelector } from '../../../hooks/useApp';
import { selectUserPrivateMoney } from '../../../reducers/user-private-money';
import { getQueryParamDefaultValues } from '../../../utils/use-query-params';
import { createColorCodedOptions } from '../../atoms/ColorCodedOption';
import SelectedPrivateMoneyDisplay from '../../organisms/SelectedPrivateMoneyDisplay';
import ThreeStateSelect from './ThreeStateSelect';

type ChecksQueryParamConfig = typeof queryParamConfigs;
export type ChecksQueryParams = {
  [key in keyof ChecksQueryParamConfig]: ChecksQueryParamConfig[key] extends QueryParamConfig<
    unknown,
    infer T
  >
    ? T
    : never;
};

type FormFilterParams = Omit<ChecksQueryParams, 'from' | 'to'> & {
  date_range: {
    created_from: moment.Moment | null;
    created_to: moment.Moment | null;
  };
};

type Organization = {
  name: string;
  code: string;
};

const MomentParam: QueryParamConfig<Moment | null | undefined> = {
  encode: (value) => (value ? value.toISOString() : null),
  decode: (value) => (value && typeof value === 'string' ? moment(value) : null),
};

const queryParamConfigs = {
  check_id: withDefault(StringParam, ''),
  created_from: withDefault(MomentParam, null),
  created_to: withDefault(MomentParam, null),
  note: withDefault(StringParam, ''),
  issuer_name: withDefault(StringParam, ''),
  organization_code: withDefault(StringParam, null),
  status: withDefault(StringParam, null),
};

const queryParamDefaultValues = getQueryParamDefaultValues(queryParamConfigs);

const FormWrapper = styled.div`
  ${LabeledTextBox} {
    margin-left: 10px;
    display: inline;
  }

  ${ThreeStateSelect} {
    margin: 5px;
    width: 200px;
  }

  ${Button} {
    margin-left: 10px;
  }

  ${Button} + ${Button} {
    margin-left: 5px;
  }

  ${Label} {
    margin-left: 10px;
  }

  ${LabeledSelect as any} {
    display: inline-block;
    ${Select as any} {
      min-width: 250px;
  }
`;

const FilterRow = styled.span`
  display: flex;
  margin-top: 10px;
  align-items: center;

  ${DateTimeRange} {
    margin-bottom: 0;
  }

  ${Labeled} {
    display: flex;
    > ${Label} {
      align-self: center;
    }
  }
`;

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 5px;
  margin: 5px 0;
`;

const NULLOP = { color: 'grey', label: '指定なし', value: null };
const toOrgOp = (o: Organization) => ({ label: o.name, value: o.code });
const toOrgOptions = (orgs: Organization[]) => [NULLOP, ...orgs.map(toOrgOp)];
const isThirdParty = (money) => (money ? money.type === 'third-party' : false);

const STATUS_OPTIONS = [
  {
    label: '指定なし',
    value: null,
    bgColor: '#D0D0D0',
  },
  {
    label: '開始前',
    value: 'not-started',
    bgColor: '#2684FF',
  },
  {
    label: '有効',
    value: 'valid',
    bgColor: '#1EAC21',
  },
  {
    label: '停止中',
    value: 'paused',
    bgColor: '#735703',
  },
  {
    label: '上限到達',
    value: 'usage-limit-reached',
    bgColor: '#525252',
  },
  {
    label: '期限切れ',
    value: 'expired',
    bgColor: '#525252',
  },
];

const formatOptionLabel = createColorCodedOptions(STATUS_OPTIONS);

const FilterForm = ({
  applyFilters,
}: {
  applyFilters: (
    params: ChecksQueryParams & {
      private_money_id: string;
    }
  ) => void;
}) => {
  const [query, setQuery] = useQueryParams({
    ...queryParamConfigs,
  });

  const formDefaultValues = {
    check_id: query.check_id ?? '',
    date_range: {
      created_from: query.created_from ?? null,
      created_to: query.created_to ?? null,
    },
    note: query.note ?? '',
    issuer_name: query.issuer_name ?? '',
    organization_code: query.organization_code ?? null,
    status: query.status ?? null,
  };

  const { handleSubmit, control, reset, register } = useForm<FormFilterParams>({
    defaultValues: formDefaultValues,
  });

  const [organizations, setOrganizations] = useState([]);

  const user = useAppSelector((state) => state.user);
  const userPrivateMoney = useAppSelector(selectUserPrivateMoney);

  useEffect(() => {
    (async () => {
      // eslint-disable-next-line no-underscore-dangle
      let _organizations;
      const issuer = userPrivateMoney.organization;
      // XXX: want to remove service user requirement
      if (user.isServiceUser()) {
        _organizations = (
          await fetchOrganizations(
            toQueryParams({ privateMoneyId: userPrivateMoney.id, per_page: 10000 })
          )
        ).rows;
      } else if (isThirdParty(userPrivateMoney)) {
        const members = (
          await fetchOrganizations(
            toQueryParams({ privateMoneyId: userPrivateMoney.id, per_page: 10000 })
          )
        ).rows;
        _organizations = [issuer].concat(members);
      } else {
        _organizations = [issuer];
      }
      setOrganizations(_organizations);

      applyFilters({
        ...query,
        private_money_id: userPrivateMoney.id,
      });

      reset(formDefaultValues);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPrivateMoney, query]);

  const onConfirm = (data) => {
    const { date_range, ...rest } = data;
    setQuery({ ...date_range, ...rest });
  };

  const clearFilters = () => {
    const { created_from, created_to, ...rest } = queryParamDefaultValues;

    setQuery({ ...queryParamDefaultValues });
    reset({
      date_range: {
        created_from,
        created_to,
      },
      ...rest,
    });
  };

  return (
    <FormWrapper>
      <SelectedPrivateMoneyDisplay />
      <FilterRow>
        {userPrivateMoney.type === 'own' ? null : (
          <Controller
            name="organization_code"
            control={control}
            render={({ field: { value, onChange } }) => (
              <LabeledSelect
                labelText="チャージ元組織"
                options={toOrgOptions(organizations)}
                value={toOrgOptions(organizations).find((opt) => opt.value === value)}
                isMulti={false}
                onChange={(opt) => onChange(opt?.value ?? null)}
                placeholder="店舗を選択してください。"
              />
            )}
          />
        )}
        <LabeledTextBox labelText="チャージ元店舗" {...register('issuer_name')} />
        <LabeledTextBox labelText="ID" {...register('check_id')} />
      </FilterRow>
      <FilterRow>
        <Controller
          name="date_range"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Labeled labelText="発行日時">
              <DateTimeRange
                dateRange={{ from: value?.created_from, to: value?.created_to }}
                setDateRange={({ from, to }) =>
                  onChange({ created_from: from ?? null, created_to: to ?? null })
                }
                validTypeFrom="invalidAfterToday"
                validTypeTo="invalidAfterToday"
                dateTimeProps={{ toProps: { showIcon: true }, fromProps: { showIcon: true } }}
              />
            </Labeled>
          )}
        />
        <LabeledTextBox labelText="用途" {...register('note')} />
        <Controller
          name="status"
          control={control}
          render={({ field: { value, onChange } }) => (
            <LabeledSelect
              labelText="状態"
              options={STATUS_OPTIONS}
              value={STATUS_OPTIONS.find((x) => x.value === value)}
              isMulti={false}
              onChange={(opt) => onChange(opt?.value ?? null)}
              formatOptionLabel={formatOptionLabel}
            />
          )}
        />
      </FilterRow>
      <ButtonsWrapper>
        <Button variant="neutral" onClick={clearFilters}>
          クリア
        </Button>
        <Button onClick={handleSubmit(onConfirm)}>検索</Button>
      </ButtonsWrapper>
    </FormWrapper>
  );
};

export default styled(FilterForm)``;
